/* ─────────────────  src/context/AuthContext.jsx  ───────────────── */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";          // ← adjust if your path differs

// ------------------------------------------------------------------

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  /* 1️⃣  Read once from localStorage */
  const [user, setUser] = useState(() => {
    try {
      const json = localStorage.getItem("welsh-user");
      const parsed = json ? JSON.parse(json) : null;
      console.log("[AuthContext] initial load →", parsed);
      return parsed;
    } catch (err) {
      console.error("[AuthContext] failed to parse localStorage", err);
      return null;
    }
  });

  /* 2️⃣  Persist whenever user changes */
  useEffect(() => {
    if (user) {
      localStorage.setItem("welsh-user", JSON.stringify(user));
      // keep legacy flag for any spots that still check it
      localStorage.setItem("isLoggedIn", "true");
      console.log("[AuthContext] user saved to localStorage →", user);
    } else {
      localStorage.removeItem("welsh-user");
      localStorage.removeItem("isLoggedIn");
      console.log("[AuthContext] user cleared from localStorage");
    }
    // notify app (and other tabs) to re-evaluate guards
    window.dispatchEvent(new Event("auth:change"));
  }, [user]);

  /* 3️⃣  Helpers --------------------------------------------------- */

  /**
   * Login:
   *   • Firebase Auth sign-in
   *   • Check that a profile exists in /users/{uid}
   */
  const login = useCallback(async (email, password) => {
    console.log("[AuthContext] login() start →", { email });
    const auth = getAuth();

    try {
      const { user: authUser } = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      console.log("[AuthContext] Firebase signIn ok →", authUser.uid);

      // check Firestore profile
      const profileRef = doc(db, "users", authUser.uid);
      const snap = await getDoc(profileRef);

      if (!snap.exists()) {
        console.warn(
          "[AuthContext] profile missing in Firestore. Rejecting login.",
        );
        // Clean up the partial sign-in
        await signOut(auth);
        throw new Error("Account not found, please sign-up first");
      }

      // success
      setUser({ uid: authUser.uid, email: authUser.email, ...snap.data() });
      return authUser;
    } catch (err) {
      console.error("[AuthContext] login() error →", err);
      throw err; // propagate to UI
    }
  }, []);

  /**
   * Signup:
   *   • Create auth account
   *   • Create profile doc (/users/{uid})
   *   • Auto-sign-in
   */
  const signup = useCallback(
    async (email, password, extra = {}) => {
      console.log("[AuthContext] signup() start →", { email, extra });
      const auth = getAuth();

      const { user: authUser } = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      await setDoc(doc(db, "users", authUser.uid), {
        ...extra,
        email,
        createdAt: serverTimestamp(),
      });

      setUser({ uid: authUser.uid, email, ...extra });
      console.log("[AuthContext] signup() complete →", authUser.uid);
      return authUser;
    },
    [],
  );

  /** Clear everything and sign-out Firebase auth */
  const logout = useCallback(async () => {
    console.log("[AuthContext] logout()");
    const auth = getAuth();
    await signOut(auth);
    setUser(null);
    // keys removed in the [user] effect & event fired there
  }, []);

  /* Expose context */
  const value = { user, login, signup, logout };

  console.log("[AuthContext] provider render → user:", user);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
/* ─────────────────────────  End  ───────────────────────── */
